import React, { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useCurrentSession, useSessionStats, useGameSessionLoading, useGameSessionError, useSessionActions } from '@/stores/gameSessionStore';
import { GameSession, type SessionStats } from '@/types';
import UnifiedQuestionDisplay from '@/components/UnifiedQuestionDisplay';
import CountdownScreen from '@/components/CountdownScreen';
import useSessionPhaseTimer from '@/hooks/useSessionPhaseTimer';
import { DEFAULT_TIMER_CONFIG } from '@/config/timerConfig';

const TeacherGameView: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const currentSession = useCurrentSession();
  const sessionStats = useSessionStats();
  const isLoading = useGameSessionLoading();
  const error = useGameSessionError();
  const {
    selectSession,
    subscribeToSession,
    unsubscribeFromSession,
    executeControl,
    proceedAfterScoring,
    transitionToLeaderboard,
    skipBonusPhase,
    clearError
  } = useSessionActions();

  const lastAdvancedPhaseRef = useRef<string | null>(null);

  // Compute fallback seconds for the shared phase timer.
  const fallbackSeconds = useMemo(() => {
    if (!currentSession) {
      return DEFAULT_TIMER_CONFIG.questionPhaseSeconds;
    }

    const phaseDuration = currentSession.currentPhaseDurationSeconds;
    const currentStage = currentSession.stages?.[currentSession.currentStageIndex];
    const activeQuestion = currentSession.questions?.[currentSession.currentQuestionIndex];

    const ensurePositive = (value: number | undefined | null, fallback: number) => {
      if (typeof value === 'number' && value > 0) {
        return value;
      }
      return fallback;
    };

    switch (currentSession.gameState) {
      case 'countdown':
        return ensurePositive(phaseDuration, 3);
      case 'question':
        return ensurePositive(
          phaseDuration,
          currentStage?.type === 'question'
            ? ensurePositive(currentStage.question?.questionReadingTimeSeconds, DEFAULT_TIMER_CONFIG.questionPhaseSeconds)
            : ensurePositive(activeQuestion?.questionReadingTimeSeconds, DEFAULT_TIMER_CONFIG.questionPhaseSeconds)
        );
      case 'running':
        return ensurePositive(
          phaseDuration,
          currentStage?.type === 'question'
            ? ensurePositive(currentStage.question?.arenaPlayTimeSeconds, DEFAULT_TIMER_CONFIG.runningPhaseSeconds)
            : ensurePositive(activeQuestion?.arenaPlayTimeSeconds, DEFAULT_TIMER_CONFIG.runningPhaseSeconds)
        );
      case 'bonus':
        return ensurePositive(
          phaseDuration,
          currentStage?.type === 'bonus'
            ? ensurePositive(currentStage.bonusRound?.selectionTimeSeconds, 8)
            : 8
        );
      default:
        return ensurePositive(phaseDuration, DEFAULT_TIMER_CONFIG.questionPhaseSeconds);
    }
  }, [currentSession]);

  // Use the working timer hook instead of broken custom logic
  const timeRemaining = useSessionPhaseTimer(currentSession, fallbackSeconds);

  useEffect(() => {
    if (sessionId && sessionId !== currentSession?.id) {
      selectSession(sessionId);
    }
  }, [sessionId, currentSession?.id, selectSession]);

  // Subscribe to Firestore updates for real-time session changes
  useEffect(() => {
    if (!sessionId) {
      return;
    }

    const unsubscribe = subscribeToSession(sessionId);

    return () => {
      unsubscribe();
      unsubscribeFromSession();
    };
  }, [sessionId, subscribeToSession, unsubscribeFromSession]);

  const handlePhaseAdvance = React.useCallback(async () => {
    if (!currentSession) return;
    try {
      await executeControl(currentSession.id, { type: 'advance_phase' });
    } catch (error) {
      console.error('Failed to advance phase:', error);
    }
  }, [currentSession, executeControl]);

  useEffect(() => {
    lastAdvancedPhaseRef.current = null;
  }, [currentSession?.currentStageIndex, currentSession?.gameState, currentSession?.currentPhaseEndTime]);

  // Auto-advance functionality for timer
  useEffect(() => {
    if (!currentSession || timeRemaining > 0 || currentSession.status !== 'active') {
      return;
    }

    if (currentSession.gameState !== 'countdown' && !currentSession.autoAdvance) {
      return;
    }

    const phaseKey = `${currentSession.id}-${currentSession.currentStageIndex}-${currentSession.gameState}-${currentSession.currentQuestionIndex}`;
    if (lastAdvancedPhaseRef.current === phaseKey) {
      return;
    }

    lastAdvancedPhaseRef.current = phaseKey;
    handlePhaseAdvance();
  }, [timeRemaining, currentSession, handlePhaseAdvance]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!currentSession?.id || !Array.isArray(currentSession.questions) || currentSession.questions.length === 0) {
      return;
    }

    try {
      const preloadKey = `arena_preload_${currentSession.id}`;
      const payload = {
        sessionId: currentSession.id,
        questions: currentSession.questions,
        currentQuestionIndex: currentSession.currentQuestionIndex ?? 0,
        gameState: currentSession.gameState,
        updatedAt: Date.now()
      };
      sessionStorage.setItem(preloadKey, JSON.stringify(payload));
    } catch (error) {
      console.error('Failed to cache arena questions for spectator view:', error);
    }
  }, [
    currentSession?.id,
    currentSession?.questions,
    currentSession?.currentQuestionIndex,
    currentSession?.gameState
  ]);

  const handleStartGame = async () => {
    if (!currentSession) return;
    try {
      await executeControl(currentSession.id, { type: 'start_game' });
    } catch (error) {
      console.error('Failed to start game:', error);
    }
  };

  const handleProceedAfterScoring = async () => {
    if (!currentSession) return;
    try {
      await proceedAfterScoring(currentSession.id);
    } catch (error) {
      console.error('Failed to proceed after scoring:', error);
    }
  };

  const handleSkipBonus = async () => {
    if (!currentSession) return;
    try {
      await skipBonusPhase(currentSession.id);
    } catch (error) {
      console.error('Failed to skip bonus phase:', error);
    }
  };

  const handleShowLeaderboard = async () => {
    if (!currentSession) return;
    try {
      await transitionToLeaderboard(currentSession.id);
    } catch (error) {
      console.error('Failed to show leaderboard:', error);
    }
  };

  if (isLoading) {
    return (
      <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
          minHeight: '100vh',
          color: 'white'
        }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '2rem',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔄</div>
            <h2>Loading Game Session...</h2>
          </div>
        </div>
    );
  }

  if (error) {
    return (
      <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
          minHeight: '100vh',
          color: 'white'
        }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '2rem',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>❌</div>
            <h2>Error Loading Game</h2>
            <p>{error}</p>
            <button
              onClick={clearError}
              style={{
                padding: '1rem 2rem',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1.1rem',
                marginTop: '1rem'
              }}
            >
              Retry
            </button>
          </div>
        </div>
    );
  }

  if (!currentSession) {
    return (
      <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
          minHeight: '100vh',
          color: 'white'
        }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '2rem',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)'
          }}>
            <h2>Game Session Not Found</h2>
            <p>Please select a valid game session from the dashboard.</p>
          </div>
        </div>
    );
  }

  // Special full-screen rendering for countdown and question phases (Kahoot-style)
  if (currentSession.gameState === 'countdown') {
    return (
      <CountdownScreen
          isTeacher={true}
          onCountdownComplete={handlePhaseAdvance}
          sessionId={currentSession.id}
          timeRemaining={timeRemaining}
        />
    );
  }

  if (currentSession.gameState === 'question') {
    return (
      <QuestionPhase
          session={currentSession}
          timeRemaining={timeRemaining}
          onAdvance={handlePhaseAdvance}
        />
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
      minHeight: '100vh',
      padding: '2rem',
      color: 'white',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Game Header - only shown in lobby */}
        {currentSession.gameState === 'lobby' && (
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '2rem',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
              🎮 {currentSession.questionSetName}
            </h1>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                backgroundColor: 'rgba(255,215,0,0.2)',
                padding: '1rem',
                borderRadius: '12px'
              }}>
                <h3 style={{ color: '#FFD700', fontSize: '2rem', margin: 0 }}>
                  {currentSession.gameCode}
                </h3>
                <p style={{ margin: '0.5rem 0 0 0' }}>Game Code</p>
              </div>

              <div style={{
                backgroundColor: 'rgba(76,175,80,0.2)',
                padding: '1rem',
                borderRadius: '12px'
              }}>
                <h3 style={{ color: '#4CAF50', fontSize: '2rem', margin: 0 }}>
                  {currentSession.maxPlayers >= 999
                    ? currentSession.players.length
                    : `${currentSession.players.length}/${currentSession.maxPlayers}`}
                </h3>
                <p style={{ margin: '0.5rem 0 0 0' }}>
                  {currentSession.maxPlayers >= 999 ? 'Players Joined' : 'Players'}
                </p>
              </div>

              <div style={{
                backgroundColor: 'rgba(33,150,243,0.2)',
                padding: '1rem',
                borderRadius: '12px'
              }}>
                <h3 style={{ color: '#2196F3', fontSize: '2rem', margin: 0 }}>
                  {currentSession.gameState.toUpperCase()}
                </h3>
                <p style={{ margin: '0.5rem 0 0 0' }}>Status</p>
              </div>
            </div>

            <div style={{
              fontSize: '1.2rem',
              backgroundColor: 'rgba(255,255,255,0.1)',
              padding: '1rem',
              borderRadius: '8px'
            }}>
              <strong>Students join at:</strong> {window.location.origin}
            </div>
          </div>
        )}

        {/* Game Phase Content */}
        {currentSession.gameState === 'lobby' && (
          <LobbyPhase
            session={currentSession}
            onStartGame={handleStartGame}
          />
        )}

        {currentSession.gameState === 'running' && (
          <ArenaPhase
            session={currentSession}
            onAdvance={handlePhaseAdvance}
          />
        )}

        {currentSession.gameState === 'scoring' && (
          <ScoringPhase
            session={currentSession}
            sessionStats={sessionStats}
            onShowLeaderboard={handleShowLeaderboard}
          />
        )}

        {currentSession.gameState === 'leaderboard' && (
          <LeaderboardPhase
            session={currentSession}
            sessionStats={sessionStats}
            onProceed={handleProceedAfterScoring}
          />
        )}

        {currentSession.gameState === 'bonus' && (
          <BonusPhase
            session={currentSession}
            timeRemaining={timeRemaining}
            onSkip={handleSkipBonus}
          />
        )}

        {currentSession.gameState === 'finished' && (
          <FinishedPhase session={currentSession} />
        )}
      </div>

      {/* Bottom left info - shown during active game phases */}
      {currentSession.gameState !== 'lobby' && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          left: '1rem',
          backgroundColor: 'rgba(0,0,0,0.6)',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          backdropFilter: 'blur(10px)',
          fontSize: '0.9rem',
          lineHeight: '1.4',
          zIndex: 100
        }}>
          <div style={{ color: '#FFD700', fontWeight: 'bold', marginBottom: '0.25rem' }}>
            Code: {currentSession.gameCode}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
            Join: {window.location.origin}
          </div>
        </div>
      )}
    </div>
  );
};

// Phase Components
const LobbyPhase: React.FC<{
  session: GameSession;
  onStartGame: () => void;
}> = ({ session, onStartGame }) => {
  const readyPlayers = session.players.filter(p => p.isReady);
  const notReadyPlayers = session.players.filter(p => !p.isReady);

  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '2rem',
      borderRadius: '20px',
      backdropFilter: 'blur(10px)'
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>
        🏠 Game Lobby
      </h2>

      {/* Status Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,193,7,0.2)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid rgba(255,193,7,0.4)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#FFC107' }}>
            {notReadyPlayers.length}
          </div>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '1rem', color: 'rgba(255,255,255,0.9)' }}>
            {notReadyPlayers.length === 1 ? 'Student Setting Up' : 'Students Setting Up'}
          </p>
        </div>

        <div style={{
          backgroundColor: 'rgba(76,175,80,0.2)',
          padding: '1.5rem',
          borderRadius: '12px',
          border: '2px solid rgba(76,175,80,0.4)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#4CAF50' }}>
            {readyPlayers.length}
          </div>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '1rem', color: 'rgba(255,255,255,0.9)' }}>
            {readyPlayers.length === 1 ? 'Player Ready' : 'Players Ready'}
          </p>
        </div>
      </div>

      {/* Ready Players */}
      {readyPlayers.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#4CAF50', marginBottom: '1rem', fontSize: '1.3rem' }}>
            ✅ Ready to Play ({readyPlayers.length})
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            {readyPlayers.map((player) => (
              <div
                key={player.id}
                style={{
                  backgroundColor: 'rgba(76,175,80,0.3)',
                  padding: '1rem',
                  borderRadius: '12px',
                  border: '2px solid #4CAF50'
                }}
              >
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✅</div>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{player.name}</h3>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                  Ready to start
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Not Ready Players */}
      {notReadyPlayers.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#FFC107', marginBottom: '1rem', fontSize: '1.3rem' }}>
            ⏳ Setting Up ({notReadyPlayers.length})
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            {notReadyPlayers.map((player) => (
              <div
                key={player.id}
                style={{
                  backgroundColor: 'rgba(255,152,0,0.3)',
                  padding: '1rem',
                  borderRadius: '12px',
                  border: '2px solid #FF9800'
                }}
              >
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{player.name}</h3>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                  Choosing character...
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1rem' }}>
          <button
            onClick={() => {
              console.log('🔄 Teacher: Manual refresh requested');
              window.location.reload();
            }}
            style={{
              padding: '0.8rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: 'rgba(33, 150, 243, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔄 Refresh Players
          </button>

          <button
            onClick={onStartGame}
            disabled={readyPlayers.length === 0}
            style={{
              padding: '1.5rem 3rem',
              fontSize: '1.5rem',
              backgroundColor: readyPlayers.length > 0 ? '#4CAF50' : 'rgba(255,255,255,0.3)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: readyPlayers.length > 0 ? 'pointer' : 'not-allowed',
              fontWeight: 'bold',
              opacity: readyPlayers.length > 0 ? 1 : 0.5
            }}
          >
            🚀 Start Game
          </button>
        </div>
        <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
          {readyPlayers.length === 0
            ? 'Waiting for at least one player to be ready...'
            : `${readyPlayers.length} ${readyPlayers.length === 1 ? 'player is' : 'players are'} ready to start!`
          }
        </p>
      </div>
    </div>
  );
};

const QuestionPhase: React.FC<{
  session: GameSession;
  timeRemaining: number;
  onAdvance: () => void;
}> = ({ session, timeRemaining, onAdvance }) => {
  const currentQuestion = session.questions[session.currentQuestionIndex];

  if (!currentQuestion) {
    return (
      <div style={{
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: '2rem',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        textAlign: 'center'
      }}>
        <h2>Loading question...</h2>
      </div>
    );
}

  return (
    <UnifiedQuestionDisplay
      question={currentQuestion}
      currentQuestionIndex={session.currentQuestionIndex}
      totalQuestions={session.questions.length}
      timeRemaining={timeRemaining}
      isTeacher={true}
      showCorrectAnswer={false}
      onAdvanceEarly={onAdvance}
      sessionId={session.id}
    />
  );
};

const ArenaPhase: React.FC<{
  session: GameSession;
  onAdvance: () => void;
}> = ({ session, onAdvance }) => {
  // Build the GameArena URL with spectator mode
  const arenaUrl = `/game?sessionId=${session.id}&spectator=true`;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 1
    }}>
      {/* Render the actual GameArena in an iframe for teacher spectator view */}
      <iframe
        src={arenaUrl}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          position: 'absolute',
          top: 0,
          left: 0
        }}
        title="Game Arena Spectator View"
      />

      <button
        onClick={onAdvance}
        style={{
          position: 'fixed',
          right: '1rem',
          bottom: '1rem',
          zIndex: 100,
          padding: '0.8rem 1.35rem',
          fontSize: '1rem',
          backgroundColor: '#2B91F3',
          color: 'white',
          border: 'none',
          borderRadius: '999px',
          cursor: 'pointer',
          fontWeight: 700,
          letterSpacing: '0.01em',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
          boxShadow: '0 3px 10px rgba(43, 145, 243, 0.3)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.backgroundColor = '#1F83E3';
          e.currentTarget.style.boxShadow = '0 5px 12px rgba(43, 145, 243, 0.35)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.backgroundColor = '#2B91F3';
          e.currentTarget.style.boxShadow = '0 3px 10px rgba(43, 145, 243, 0.3)';
        }}
      >
        Show Results
      </button>
    </div>
  );
};

const ScoringPhase: React.FC<{
  session: GameSession;
  sessionStats: SessionStats | null;
  onShowLeaderboard: () => void;
}> = ({ session, onShowLeaderboard }) => {
  const currentQuestion = session.questions[session.currentQuestionIndex];
  const answerChoices = currentQuestion?.choices ?? [];
  const correctAnswerIndex = currentQuestion?.correctAnswerIndex ?? -1;
  const answerStats = session.currentAnswerStats || [];

  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '2rem',
      borderRadius: '20px',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2>📊 Scoring Phase - Question Review</h2>
        <div style={{ fontSize: '1.2rem' }}>
          Results for Question {session.currentQuestionIndex + 1}
        </div>
      </div>

      {currentQuestion && (
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', textAlign: 'center' }}>
            {currentQuestion.text}
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            {answerChoices.map((answer, index) => {
              const studentCount = answerStats[index] || 0;
              const percentage = session.players.length > 0
                ? Math.round((studentCount / session.players.length) * 100)
                : 0;
              const isCorrect = index === correctAnswerIndex;

              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: isCorrect
                      ? 'rgba(76,175,80,0.3)'
                      : 'rgba(255,255,255,0.1)',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: isCorrect
                      ? '3px solid #4CAF50'
                      : '1px solid rgba(255,255,255,0.2)',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <div style={{ marginBottom: '1rem' }}>{answer}</div>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    color: isCorrect ? '#4CAF50' : '#FFA726'
                  }}>
                    {studentCount} ({percentage}%)
                  </div>
                  {isCorrect && (
                    <div style={{ color: '#4CAF50', marginTop: '0.5rem', fontWeight: 'bold' }}>
                      ✓ CORRECT ANSWER
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ textAlign: 'center' }}>
        <p style={{ marginBottom: '1rem', fontSize: '1.1rem', color: 'rgba(255,255,255,0.9)' }}>
          Students can see their individual results and are viewing the leaderboard.
        </p>
        <button
          onClick={onShowLeaderboard}
          style={{
            padding: '1.5rem 3rem',
            fontSize: '1.3rem',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          📊 View Leaderboard
        </button>
      </div>
    </div>
  );
};

const LeaderboardPhase: React.FC<{
  session: GameSession;
  sessionStats: SessionStats | null;
  onProceed: () => void;
}> = ({ session, onProceed }) => {
  const isLastQuestion = session.currentQuestionIndex >= session.questions.length - 1;

  // Calculate leaderboard data (same logic as student view)
  const leaderboardData = session.players
    .map(player => ({
      playerId: player.id,
      name: player.name,
      totalScore: player.score || 0,
      roundScores: player.roundScores || [],
      isCurrentPlayer: false
    }))
    .sort((a, b) => b.totalScore - a.totalScore);

  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '2rem',
      borderRadius: '20px',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: '#FFD700', fontSize: '2rem' }}>🏆 Leaderboard - Round {session.currentQuestionIndex + 1}</h2>
        <div style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)', marginTop: '0.5rem' }}>
          {session.currentQuestionIndex === 0
            ? 'Scores from this round only'
            : `Cumulative scores from Rounds 1-${session.currentQuestionIndex + 1}`
          }
        </div>
      </div>

      <div style={{
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        maxWidth: '800px',
        margin: '0 auto 2rem'
      }}>
        {leaderboardData.map((player, index) => (
          <div key={player.playerId || index} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.2rem',
            backgroundColor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'rgba(255,255,255,0.1)',
            color: index <= 2 ? '#000' : '#fff',
            borderRadius: '12px',
            marginBottom: '0.8rem',
            transition: 'transform 0.3s ease',
            animation: index <= 2 ? `slideUp 0.5s ease ${index * 0.1}s both` : 'none'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}>
              <span style={{
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                {index === 0 && '🏆'}
                {index === 1 && '🥈'}
                {index === 2 && '🥉'}
                #{index + 1} {player.name}
              </span>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                {player.totalScore} pts
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center' }}>
        <p style={{ marginBottom: '1.5rem', fontSize: '1.2rem', color: 'rgba(255,255,255,0.9)' }}>
          {isLastQuestion
            ? '🎯 This was the final question! Ready to finish the game?'
            : '📚 Students are viewing the leaderboard. Ready to continue?'
          }
        </p>
        <button
          onClick={onProceed}
          style={{
            padding: '1.5rem 3rem',
            fontSize: '1.3rem',
            backgroundColor: isLastQuestion ? '#FF6B35' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          {isLastQuestion ? '🏁 Finish Game' : '➡️ Start Next Question'}
        </button>
      </div>
    </div>
  );
};

const BonusPhase: React.FC<{
  session: GameSession;
  timeRemaining: number;
  onSkip: () => void;
}> = ({ timeRemaining, onSkip }) => {
  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '2rem',
      borderRadius: '20px',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2>⚡ Bonus Phase</h2>
        <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
          Students are selecting bonus items
        </div>
        <div style={{
          fontSize: '3rem',
          color: timeRemaining <= 5 ? '#FF6B6B' : '#FFD700',
          fontWeight: 'bold'
        }}>
          {timeRemaining}s
        </div>
      </div>

      <div style={{
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚡🎁</div>
        <p style={{ fontSize: '1.3rem' }}>
          Students are selecting their bonus items for the next round
        </p>
      </div>

      <div style={{ textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <button
          onClick={onSkip}
          style={{
            padding: '1rem 2rem',
            fontSize: '1.2rem',
            backgroundColor: '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          ⏭️ Skip Bonus
        </button>
      </div>
    </div>
  );
};

const FinishedPhase: React.FC<{
  session: GameSession;
}> = ({ session }) => {
  return (
    <div style={{
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '2rem',
      borderRadius: '20px',
      backdropFilter: 'blur(10px)',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏆</div>
      <h2>Game Complete!</h2>
      <p style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>
        Congratulations to all players!
      </p>

      <div style={{
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: '2rem',
        borderRadius: '12px'
      }}>
        <h3>Final Results</h3>
        {session.players
          .sort((a, b) => b.score - a.score)
          .map((player, index) => (
            <div
              key={player.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                backgroundColor: index === 0
                  ? 'rgba(255,215,0,0.3)'
                  : 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                marginBottom: '0.5rem'
              }}
            >
              <div>
                <span style={{ fontSize: '1.5rem', marginRight: '1rem' }}>
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'}
                </span>
                {player.name}
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                {player.score} pts
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default TeacherGameView;
